import React, { forwardRef } from 'react'
import { Box, Card, Skeleton, Typography } from '@material-ui/core'
import { alpha, useTheme } from '@material-ui/core/styles'

// ----------------------------------------------------------------------
// The card shell shared by Home's "Ready to analyse" and "In progress": the
// theme Card (hairline border, radius, no shadow) as on the Files page, a header
// with the title and a quiet count, a list of 44px rows with hairline separators,
// and an optional footer pinned to the bottom so two cards side by side line up.
// ----------------------------------------------------------------------

export const ROW_HEIGHT = 44
// Horizontal padding of the header, rows and footer.
export const CARD_PX = 2.5

// The list around the rows: a grid whose columns every row shares (each row is a
// subgrid spanning them all and carries `data-row`), so the columns line up from
// row to row. Separators sit between rows, inset to the text, so the card edge
// stays clean.
export const listSx = (theme, columns) => ({
  display: 'grid',
  gridTemplateColumns: columns,
  columnGap: 1.5,
  alignContent: 'start',
  '& > [data-row]': { position: 'relative', gridColumn: '1 / -1' },
  '& > [data-row] + [data-row]::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: theme.spacing(CARD_PX),
    right: theme.spacing(CARD_PX),
    height: '1px',
    backgroundColor: theme.palette.divider,
    pointerEvents: 'none',
  },
})

// One row of a list: a subgrid of the list's columns.
export const rowSx = (theme, { interactive = false } = {}) => ({
  display: 'grid',
  gridTemplateColumns: 'subgrid',
  alignItems: 'center',
  minHeight: ROW_HEIGHT,
  px: CARD_PX,
  width: '100%',
  boxSizing: 'border-box',
  borderRadius: 0,
  textAlign: 'left',
  color: 'text.primary',
  textDecoration: 'none',
  ...(interactive
    ? {
        cursor: 'pointer',
        transition: theme.transitions.create('background-color', { duration: 120 }),
        '&:hover': { bgcolor: 'action.hover' },
        '&:focus-visible, &.Mui-focusVisible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: -2,
        },
      }
    : {}),
})

// Secondary data in a row: type labels, dates, counts.
export const metaSx = {
  fontSize: 13,
  lineHeight: 1.4,
  color: 'text.secondary',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontVariantNumeric: 'tabular-nums',
  minWidth: 0,
}

export const RowSkeletons = ({ count = 3 }) => (
  <Box aria-hidden>
    {Array.from({ length: count }).map((_, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <Box key={index} sx={{ height: ROW_HEIGHT, px: CARD_PX, display: 'flex', alignItems: 'center' }}>
        <Skeleton variant="text" sx={{ width: `${70 - index * 12}%`, height: 20 }} />
      </Box>
    ))}
  </Box>
)

// A centred two-line message for a card with nothing to list.
export const CardMessage = ({ title, detail, action }) => (
  <Box
    sx={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      gap: 0.5,
      px: CARD_PX,
      py: 4,
    }}
  >
    <Typography sx={{ fontSize: 14, fontWeight: 500, color: 'text.primary' }}>{title}</Typography>
    {detail && <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.45 }}>{detail}</Typography>}
    {action && <Box sx={{ mt: 1.5 }}>{action}</Box>}
  </Box>
)

const HomeCard = forwardRef(function HomeCard(
  { title, titleId, count, footer, children, highlighted = false, overlay = null, sx, ...other },
  ref,
) {
  const theme = useTheme()
  return (
    <Card
      ref={ref}
      component="section"
      aria-labelledby={titleId}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        minHeight: 196,
        transition: theme.transitions.create(['border-color', 'box-shadow', 'background-color'], { duration: 120 }),
        ...(highlighted
          ? {
              borderColor: 'primary.main',
              boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
              bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.04 : 0.06),
            }
          : {}),
        ...sx,
      }}
      {...other}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 2,
          px: CARD_PX,
          pt: 2,
          pb: 1,
        }}
      >
        <Typography id={titleId} component="h2" sx={{ fontSize: 16, fontWeight: 600, lineHeight: 1.5 }}>
          {title}
        </Typography>
        {count && (
          <Typography
            sx={{ fontSize: 13, color: 'text.secondary', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
          >
            {count}
          </Typography>
        )}
      </Box>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>{children}</Box>
      {footer && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            columnGap: 1.5,
            rowGap: 0.5,
            minHeight: 52,
            px: CARD_PX,
            py: 1,
          }}
        >
          {footer}
        </Box>
      )}
      {overlay}
    </Card>
  )
})

export default HomeCard
